import { Container, CTABand, Eyebrow } from "@constructorfleet/ui/design-system";

export function AboutPage() {
  return (
    <div className="about-page">
      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="about-hero">
        <Container>
          <Eyebrow brand dot>
            About ConstructorFleet
          </Eyebrow>
          <h1>Built by two engineers who understand the problem.</h1>
          <p className="lede">
            ConstructorFleet is a small, focused company. We build automation for the daily
            obligations that carry real consequences when they&apos;re missed — and we keep it
            reliable, documented, and simple.
          </p>
        </Container>
      </section>

      {/* ── Founders ────────────────────────────────────────────── */}
      <section className="founders">
        <Container>
          <div className="founder-grid">
            <div className="founder">
              <div className="portrait">
                <img
                  src="/alan.webp"
                  alt="Portrait of Alan"
                  width={96}
                  height={96}
                  loading="lazy"
                />
              </div>
              <div className="meta">
                <span className="role">Co-founder</span>
                <h2>Alan</h2>
                <p>
                  Twenty-plus years in systems and infrastructure automation. Alan&apos;s focus is
                  the operational side — taking repetitive manual work and making it run reliably,
                  predictably, and without surprises.
                </p>
                <div className="stats">
                  <div className="stat">
                    <b>20+ yrs</b>
                    <span>Systems &amp; infra</span>
                  </div>
                  <div className="stat">
                    <b>Operations</b>
                    <span>Reliability focus</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="founder b">
              <div className="portrait">
                <img
                  src="/teagan.webp"
                  alt="Portrait of Teagan"
                  width={96}
                  height={96}
                  loading="lazy"
                />
              </div>
              <div className="meta">
                <span className="role">Co-founder</span>
                <h2>Teagan</h2>
                <p>
                  Twenty-plus years building software that needs to hold up under real-world
                  pressure. Teagan&apos;s focus is making the critical workflow work cleanly, every
                  time, with a clear record of what happened.
                </p>
                <div className="stats">
                  <div className="stat">
                    <b>20+ yrs</b>
                    <span>Software engineering</span>
                  </div>
                  <div className="stat">
                    <b>Product</b>
                    <span>Workflow focus</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Why ─────────────────────────────────────────────────── */}
      <section className="why-section">
        <Container>
          <div className="inner">
            <div>
              <Eyebrow brand dot>
                Why we built this
              </Eyebrow>
              <h2>We kept seeing the same problem.</h2>
            </div>
            <div className="p-block">
              <p>
                People managing serious daily obligations by hand — dialing in, checking portals,
                tracking deadlines — even though the steps were predictable and{" "}
                <b>the consequences of missing one were real</b>.
              </p>
              <p>
                We built ConstructorFleet to close that gap. Each tool handles a specific repeated
                task, keeps a clear record that it happened, and reduces the ways a routine process
                can fail for avoidable reasons.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Beliefs ─────────────────────────────────────────────── */}
      <section className="beliefs">
        <Container>
          <div className="beliefs-head">
            <Eyebrow brand dot>
              What we believe
            </Eyebrow>
            <h2>Three principles we hold to.</h2>
          </div>
          <div className="beliefs-grid">
            <div className="belief">
              <div className="big">01</div>
              <h3>Do one thing well.</h3>
              <p>
                We&apos;d rather build one workflow that holds up every day than a broad platform
                full of half-finished features.
              </p>
            </div>
            <div className="belief">
              <div className="big">02</div>
              <h3>Records matter.</h3>
              <p>
                When something runs, you should be able to see what happened, when it happened, and
                what the result was. Every time.
              </p>
            </div>
            <div className="belief">
              <div className="big">03</div>
              <h3>Stay close to the problem.</h3>
              <p>
                The people building the product are the same people making the decisions.
                There&apos;s no gap between what you need and what gets built.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────── */}
      <section className="section" style={{ paddingTop: 0 }}>
        <Container>
          <CTABand
            title="Have a daily obligation you'd like automated?"
            lede="We're building one tool at a time. If our approach fits your situation,
              let's talk."
            ctas={[
              { label: "See Pricing", href: `/pricing`, variant: "brand" },
              {
                label: "Get in touch",
                href: `mailto:hello@constructorfleet.com`,
                variant: "secondary",
              },
            ]}
          />
        </Container>
      </section>
    </div>
  );
}
